/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {combineEpics, Epic} from 'redux-observable';
import {filter, ignoreElements, map, mapTo, mergeMap, tap} from 'rxjs/operators';
import {isActionOf} from 'typesafe-actions';
import {
	flexServiceWorkerRegistrationSceneActivated,
	flexServiceWorkerRegistrationSceneDeactivated,
	flexServiceWorkerRegistrationSceneEnterPasswordUnexpectedError,
	flexServiceWorkerRegistrationSceneEnterPasswordValidationException,
	flexServiceWorkerRegistrationSceneEnterPhoneNumberValidationException,
	flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeUnexpectedError,
	flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeValidationException,
	flexServiceWorkerRegistrationScenePasswordInputChanged,
	flexServiceWorkerRegistrationScenePasswordStrengthEvaluated,
	flexServiceWorkerRegistrationScenePasswordSubmitted,
	flexServiceWorkerRegistrationScenePhoneNumberLookupInputChanged,
	flexServiceWorkerRegistrationScenePhoneNumberLookupResolved,
	flexServiceWorkerRegistrationScenePhoneNumberLookupSubmitted,
	flexServiceWorkerRegistrationScenePhoneVerificationCodeSubmitted,
	flexServiceWorkerRegistrationSceneRequestResolvePersonalName,
	flexServiceWorkerRegistrationSceneRequestStopPhoneVerification,
	flexServiceWorkerRegistrationSceneResolvePersonalNameUnexpectedError,
	flexServiceWorkerRegistrationSceneStateChanged,
	flexServiceWorkerRegistrationSceneStateRetrieved,
	flexServiceWorkerRegistrationSceneStopPhoneVerificationUnexpectedError
} from '../scenes/FlexServiceWorkerRegistration/actions';
import {flexServiceWorkerRegistrationResource, iamStompClient, loginService, phoneNumberLookupResource} from '../services';
import {FLEX_SERVICE_WORKER_REGISTRATION_STATE_TOPIC_PREFIX} from '../services/Stomp/topics';
import {stompClientSubscribedToTopic, stompClientUnsubscribedFromTopic, stompMessageReceived} from '../services/Stomp/actions';
import {phoneNumberNumeric} from '../model/formatting';
import {
	iamApiFlexServiceWorkerRegistrationEnterPasswordUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPasswordValidationException,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberValidationException,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeValidationException,
	iamApiFlexServiceWorkerRegistrationPasswordEntered,
	iamApiFlexServiceWorkerRegistrationPasswordStrengthEvaluated,
	iamApiFlexServiceWorkerRegistrationResolvePersonalNameUnexpectedError,
	iamApiFlexServiceWorkerRegistrationStateRetrieved,
	iamApiFlexServiceWorkerRegistrationStopPhoneVerificationUnexpectedError,
	iamApiPhoneNumberLookupPerformed
} from '../services/IamApi/actions';
import {violationsWithErrorMessages} from '../model/validation';
import {
	FLEX_SERVICE_WORKER_REGISTRATION_SECURITY_CONTEXT_AUTHENTICATED,
	STATE_CHANGE_FOR_FLEX_SERVICE_WORKER_REGISTRATION
} from '../services/Stomp/EventType';
import {FlexServiceWorkerRegistrationStateDTO} from '../generated/iam-client';
import {Route as LoginUIRoute} from '../routes';
import {BehaviorSubject, combineLatest, EMPTY} from 'rxjs';
import {AuthenticationData} from '../services/LoginService/LoginService';
import {navigate} from '../components/OutHook';

const flexServiceWorkerRegistrationIdFromQueryString: () => string =
	() => new URLSearchParams(window.document.location.search).get('flexServiceWorkerRegistrationId') || '';

const subscribeToFlexServiceWorkerRegistrationStateTopicOnSceneActivate: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationSceneActivated)),
	mergeMap(() => iamStompClient.subscribe(
		FLEX_SERVICE_WORKER_REGISTRATION_STATE_TOPIC_PREFIX + flexServiceWorkerRegistrationIdFromQueryString()
	))
);

// noinspection DuplicatedCode
const unsubscribeFromFlexServiceWorkerRegistrationStateTopicOnSceneDeactivate: Epic = (action$) => {
	let subscribedTopic: string | null = null;

	action$.pipe(
		filter(isActionOf(stompClientSubscribedToTopic)),
		filter((action) => action.payload.topic.startsWith(FLEX_SERVICE_WORKER_REGISTRATION_STATE_TOPIC_PREFIX))
	).subscribe((action) => subscribedTopic = action.payload.topic);

	action$.pipe(
		filter(isActionOf(stompClientUnsubscribedFromTopic)),
		filter((action) => action.payload.topic === subscribedTopic)
	).subscribe(() => subscribedTopic = null);

	return action$.pipe(
		filter(isActionOf(flexServiceWorkerRegistrationSceneDeactivated)),
		filter(() => subscribedTopic !== null),
		mergeMap(() => iamStompClient.unsubscribe(String(subscribedTopic)))
	);
};

const retrieveFlexServiceWorkerRegistrationStateOnSceneActivate: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationSceneActivated)),
	mergeMap(() => flexServiceWorkerRegistrationResource.flexServiceWorkerRegistrationState(flexServiceWorkerRegistrationIdFromQueryString()))
);

const performPhoneNumberLookupOnRetrievedStateWithEnteredPhoneNumber: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	mergeMap((action) => {
		if (!action.payload.state.enteredPhoneNumber)
			return EMPTY;
		const enteredPhoneNumber = action.payload.state.enteredPhoneNumber;
		return phoneNumberLookupResource.phoneNumberLookup(enteredPhoneNumber.country, phoneNumberNumeric(enteredPhoneNumber.phoneNumber));
	})
);

const performPhoneNumberLookupOnInputChanged: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationScenePhoneNumberLookupInputChanged)),
	mergeMap((action) => phoneNumberLookupResource.phoneNumberLookup(action.payload.country, phoneNumberNumeric(action.payload.phoneNumber)))
);

const enterPhoneNumberOnPhoneNumberLookupSubmitted: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationScenePhoneNumberLookupSubmitted)),
	mergeMap((action) => flexServiceWorkerRegistrationResource.enterPhoneNumber(
		flexServiceWorkerRegistrationIdFromQueryString(), action.payload.command
	))
);

const enterVerificationCodeOnVerificationCodeSubmitted: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationScenePhoneVerificationCodeSubmitted)),
	mergeMap((action) => flexServiceWorkerRegistrationResource.enterPhoneVerificationCode(
		flexServiceWorkerRegistrationIdFromQueryString(), action.payload.command
	))
);

const requestPhoneVerificationStopOnVerificationStopRequested: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationSceneRequestStopPhoneVerification)),
	mergeMap(() => flexServiceWorkerRegistrationResource.requestPhoneVerificationStop(flexServiceWorkerRegistrationIdFromQueryString()))
);

const resolvePersonalNameOnResolvePersonalNameSubmitted: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationSceneRequestResolvePersonalName)),
	mergeMap(() => flexServiceWorkerRegistrationResource.resolvePersonalName(flexServiceWorkerRegistrationIdFromQueryString()))
);

const evaluatePasswordStrengthOnPasswordChanged: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationScenePasswordInputChanged)),
	mergeMap((action) => flexServiceWorkerRegistrationResource.evaluatePasswordStrength(
		flexServiceWorkerRegistrationIdFromQueryString(), action.payload.evaluationRequest
	))
);

const enterPasswordOnPasswordSubmitted: Epic = (action$) => action$.pipe(
	filter(isActionOf(flexServiceWorkerRegistrationScenePasswordSubmitted)),
	mergeMap((action) => flexServiceWorkerRegistrationResource.enterPassword(
		flexServiceWorkerRegistrationIdFromQueryString(), action.payload.command
	))
);

const notifySceneOfRetrievedState: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	map((action) => flexServiceWorkerRegistrationSceneStateRetrieved(action.payload)));

const notifySceneOfStateChange: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter((action) => action.payload.eventType === STATE_CHANGE_FOR_FLEX_SERVICE_WORKER_REGISTRATION),
	map((action) => flexServiceWorkerRegistrationSceneStateChanged({
		state:
			action.payload.eventBody as unknown as FlexServiceWorkerRegistrationStateDTO
	}))
);

const notifySceneOfResolvedPhoneNumberLookup: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiPhoneNumberLookupPerformed)),
	filter((action) => action.payload.result.found),
	map((action) => {
		if (!action.payload.result.phoneNumber)
			throw 'Phone number MUST be defined for lookup result with found=true';
		return flexServiceWorkerRegistrationScenePhoneNumberLookupResolved({phoneNumber: action.payload.result.phoneNumber});
	})
);

const notifySceneOfEnterPhoneNumberValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPhoneNumberValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return flexServiceWorkerRegistrationSceneEnterPhoneNumberValidationException({
			violations: {
				phoneNumberLookup: violations.phoneNumberLookup || []
			}
		});
	})
);

const notifySceneOfEnterPhoneNumberUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError)),
	mapTo(iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError())
);

const notifySceneOfRequestPhoneVerificationStopUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStopPhoneVerificationUnexpectedError)),
	mapTo(flexServiceWorkerRegistrationSceneStopPhoneVerificationUnexpectedError())
);

const notifySceneOfEnterVerificationCodeValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeValidationException({
			violations: {
				verificationCode: violations.verificationCode || []
			}
		});
	})
);

const notifySceneOfEnterPhoneVerificationCodeUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeUnexpectedError)),
	mapTo(flexServiceWorkerRegistrationSceneEnterPhoneVerificationCodeUnexpectedError())
);

const notifySceneOfResolvePersonalNameUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationResolvePersonalNameUnexpectedError)),
	mapTo(flexServiceWorkerRegistrationSceneResolvePersonalNameUnexpectedError())
);

const notifySceneOfPasswordStrengthEvaluated: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationPasswordStrengthEvaluated)),
	map((action) => flexServiceWorkerRegistrationScenePasswordStrengthEvaluated(action.payload))
);

const notifySceneOfEnterPasswordValidationError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPasswordValidationException)),
	map((action) => {
		const violations = violationsWithErrorMessages(action.payload.violations);

		return flexServiceWorkerRegistrationSceneEnterPasswordValidationException({
			violations: {
				password: violations.password || []
			}
		});
	})
);

const notifySceneOfEnterPasswordUnexpectedError: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationEnterPasswordUnexpectedError)),
	map(() => flexServiceWorkerRegistrationSceneEnterPasswordUnexpectedError())
);

const navigateToLoginOnRetrievedStateWithEmailOwnershipDenied: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	filter((action) => action.payload.state.emailOwnershipDenied),
	tap(() => navigate(LoginUIRoute.LOGIN)),
	ignoreElements()
);

const navigateToLoginOnRetrievedStateWithRegistrationEnded: Epic = (action$) => action$.pipe(
	filter(isActionOf(iamApiFlexServiceWorkerRegistrationStateRetrieved)),
	filter((action) => (
		action.payload.state.registrationFailed || action.payload.state.registrationCompleted || action.payload.state.registrationExpired
	)),
	tap(() => navigate(LoginUIRoute.LOGIN)),
	ignoreElements()
);

const navigateToLoginOnStateChangeWithRegistrationFailedOrExpired: Epic = (action$) => action$.pipe(
	filter(isActionOf(stompMessageReceived)),
	filter(
		(action) => {
			const eventBody: FlexServiceWorkerRegistrationStateDTO = action.payload.eventBody as unknown as FlexServiceWorkerRegistrationStateDTO;
			return action.payload.eventType === STATE_CHANGE_FOR_FLEX_SERVICE_WORKER_REGISTRATION &&
				(eventBody.registrationFailed || eventBody.registrationExpired);
		}
	),
	tap(() => navigate(LoginUIRoute.LOGIN)),
	ignoreElements()
);

const redirectOnSecurityContextAuthenticated: Epic = (action$) => {
	const authRequestPendingSubject = new BehaviorSubject(false);

	action$.pipe(filter(isActionOf(flexServiceWorkerRegistrationScenePasswordSubmitted)))
		.subscribe(() => authRequestPendingSubject.next(true));

	action$.pipe(filter(isActionOf(iamApiFlexServiceWorkerRegistrationPasswordEntered)))
		.subscribe(() => authRequestPendingSubject.next(false));

	const contextAuthenticatedObservable = action$.pipe(
		filter(isActionOf(stompMessageReceived)),
		filter((action) => action.payload.eventType === FLEX_SERVICE_WORKER_REGISTRATION_SECURITY_CONTEXT_AUTHENTICATED)
	);

	return combineLatest([authRequestPendingSubject, contextAuthenticatedObservable])
		.pipe(
			tap(([authRequestPending, contextAuthenticatedEvent]) => {
				if (!authRequestPending) {
					const authenticationData: AuthenticationData =
						contextAuthenticatedEvent.payload.eventBody as unknown as AuthenticationData;
					loginService.login(authenticationData);
				}
			}),
			ignoreElements()
		);
};

const flexServiceWorkerRegistrationEpics: Epic = combineEpics(
	subscribeToFlexServiceWorkerRegistrationStateTopicOnSceneActivate,
	unsubscribeFromFlexServiceWorkerRegistrationStateTopicOnSceneDeactivate,
	retrieveFlexServiceWorkerRegistrationStateOnSceneActivate,
	performPhoneNumberLookupOnRetrievedStateWithEnteredPhoneNumber,
	performPhoneNumberLookupOnInputChanged,
	enterPhoneNumberOnPhoneNumberLookupSubmitted,
	enterVerificationCodeOnVerificationCodeSubmitted,
	requestPhoneVerificationStopOnVerificationStopRequested,
	resolvePersonalNameOnResolvePersonalNameSubmitted,
	evaluatePasswordStrengthOnPasswordChanged,
	enterPasswordOnPasswordSubmitted,
	notifySceneOfRetrievedState,
	notifySceneOfStateChange,
	notifySceneOfResolvedPhoneNumberLookup,
	notifySceneOfEnterPhoneNumberValidationError,
	notifySceneOfEnterPhoneNumberUnexpectedError,
	notifySceneOfRequestPhoneVerificationStopUnexpectedError,
	notifySceneOfEnterVerificationCodeValidationError,
	notifySceneOfEnterPhoneVerificationCodeUnexpectedError,
	notifySceneOfResolvePersonalNameUnexpectedError,
	notifySceneOfPasswordStrengthEvaluated,
	notifySceneOfEnterPasswordValidationError,
	notifySceneOfEnterPasswordUnexpectedError,
	navigateToLoginOnRetrievedStateWithEmailOwnershipDenied,
	navigateToLoginOnRetrievedStateWithRegistrationEnded,
	navigateToLoginOnStateChangeWithRegistrationFailedOrExpired,
	redirectOnSecurityContextAuthenticated
);

export default flexServiceWorkerRegistrationEpics;
